/**
 * The pure pieces of the Brand Presence issue list: how a page names itself in a row, how long an issue has been open, what the count counts, and the
 * colour each priority pill wears. Everything here is a function of its arguments, so the screen's
 * tests assert these shapes without mounting the screen.
 */
import { IssueStatus, TaxonomyTermPriority } from '@edgebox/sync-core-data-definitions';

/** The separators a crawled title sets its site's name apart with: a bar, a hyphen, an en or an em dash. */
const TITLE_SEGMENT_SEPARATOR = /\s+[|\-–—]\s+/;

/**
 * A page title without its site's own name, where the title carries it as its first or its last
 * segment, such as "Why we flattened the plate twice | Runners High". Only a segment that is the
 * site's name goes, compared without regard to case, so a title whose segments name the page
 * itself keeps every one of them; a title that is nothing but the name, or a page whose site is
 * not known, keeps its title whole.
 */
export function withoutSiteName({ title, siteName }: { title: string; siteName: string | undefined }): string {
  const name = siteName?.trim().toLowerCase();
  if (!name) {
    return title;
  }

  const segments = title.split(TITLE_SEGMENT_SEPARATOR);
  if (segments.length < 2) {
    return title;
  }

  const separators = title.match(new RegExp(TITLE_SEGMENT_SEPARATOR.source, 'g')) ?? [];
  if (segments[segments.length - 1].trim().toLowerCase() === name) {
    return rejoinSegments({ segments: segments.slice(0, -1), separators: separators.slice(0, -1) }) || title;
  }
  if (segments[0].trim().toLowerCase() === name) {
    return rejoinSegments({ segments: segments.slice(1), separators: separators.slice(1) }) || title;
  }
  return title;
}

/** The segments of a title set back together with the separators that stood between them. */
function rejoinSegments({ segments, separators }: { segments: string[]; separators: string[] }): string {
  return segments
    .map((segment, index) => (index === 0 ? segment : `${separators[index - 1]}${segment}`))
    .join('')
    .trim();
}

/** The path part of a page's URL, which is what tells two pages of one site apart. */
export function urlDisplayPath(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}` || url;
  } catch {
    return url;
  }
}

/** A day, in the milliseconds a timestamp counts. */
const DAY_MS = 24 * 60 * 60 * 1_000;

/** How many whole days lie between a moment and now, for a value that carries a timestamp. */
export function daysSince(value: { valueOf: () => number } | undefined, now: number): number | undefined {
  const timestamp = value?.valueOf();
  if (typeof timestamp !== 'number' || !Number.isFinite(timestamp)) {
    return undefined;
  }

  return Math.max(0, Math.floor((now - timestamp) / DAY_MS));
}

/**
 * What the count above the list counts, named so the number and the rows are one statement: the
 * status the list is narrowed to is part of the noun, and one issue reads in the singular.
 */
export function issueCountLabel(total: number, status: IssueStatus | undefined): string {
  const noun = total === 1 ? 'issue' : 'issues';
  if (!status) {
    return `${total} ${noun}`;
  }

  const byStatus: Record<IssueStatus, string> = {
    [IssueStatus.Open]: `${total} open ${noun}`,
    [IssueStatus.Fixed]: `${total} fixed ${noun}`,
    [IssueStatus.Ignored]: `${total} ignored ${noun}`,
    [IssueStatus.FalsePositive]: `${total} ${noun} marked not an issue`,
  };
  return byStatus[status];
}

/**
 * The colour each issue priority pill wears, by level, in the Brand Presence severity palette the
 * health badges already speak. Exhaustive, so a priority added to the enum fails the build here.
 */
export const ISSUE_PRIORITY_PILL_CLASSES: Record<TaxonomyTermPriority, string> = {
  [TaxonomyTermPriority.Critical]: 'bg-rose-50 text-rose-700 ring-rose-200/80',
  [TaxonomyTermPriority.High]: 'bg-orange-50 text-orange-700 ring-orange-200/80',
  [TaxonomyTermPriority.Medium]: 'bg-amber-50 text-amber-700 ring-amber-200/80',
  [TaxonomyTermPriority.Low]: 'bg-emerald-50 text-emerald-700 ring-emerald-200/80',
};

/**
 * The readable form of an evidence item's offending value, where it has one. Objects and arrays
 * stay unrendered — the reasoning beside them already says what was found — and a value the check
 * cut short keeps its ellipsis.
 */
export function evidenceValueText(value: unknown, isTruncated: boolean | null | undefined): string | undefined {
  if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
    return undefined;
  }

  const text = String(value).trim();
  if (!text) {
    return undefined;
  }

  return isTruncated ? `${text}…` : text;
}
